import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import WaveSurfer from "wavesurfer.js";
import { API_BASE } from "config/api";

const proxiedAudioUrl = (originalUrl) => {
  if (!originalUrl) return originalUrl;
  const url = new URL("api/audio/proxy", API_BASE);
  url.searchParams.set("url", originalUrl);
  return url.toString();
};

const formatTs = (s) => {
  if (s == null || Number.isNaN(s)) return "00:00:000";
  const totalMs = Math.round(s * 1000);
  const m = Math.floor(totalMs / 60000);
  const sec = Math.floor((totalMs % 60000) / 1000);
  const ms = totalMs % 1000;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}:${String(ms).padStart(
    3,
    "0"
  )}`;
};

export default function WaveformPlayer({ src, height }) {
  const hiddenAudioRef = useRef(null);
  const waveContainerRef = useRef(null);
  const wsRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [waveReady, setWaveReady] = useState(false);
  const [waveError, setWaveError] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!waveContainerRef.current || !hiddenAudioRef.current) return undefined;

    setWaveReady(false);
    setWaveError(null);

    const ws = WaveSurfer.create({
      container: waveContainerRef.current,
      media: hiddenAudioRef.current,
      waveColor: "#bdbdbd",
      progressColor: "#1976d2",
      cursorColor: "#1565c0",
      height,
      barWidth: 2,
      barGap: 1,
      barRadius: 1,
      normalize: true,
    });

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onFinish = () => setIsPlaying(false);
    const onReady = () => {
      setWaveReady(true);
      setDuration(ws.getDuration() || 0);
    };
    const onTimeUpdate = (t) => setCurrentTime(t || 0);
    const onError = (err) => {
      console.error("WaveSurfer error:", err);
      setWaveError(String(err?.message || err));
    };

    ws.on("play", onPlay);
    ws.on("pause", onPause);
    ws.on("finish", onFinish);
    ws.on("ready", onReady);
    ws.on("timeupdate", onTimeUpdate);
    ws.on("error", onError);

    wsRef.current = ws;

    return () => {
      wsRef.current = null;
      setWaveReady(false);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      ws.destroy();
    };
  }, [src, height]);

  const togglePlay = () => {
    if (wsRef.current) {
      if (wsRef.current.isPlaying()) wsRef.current.pause();
      else wsRef.current.play();
      return;
    }
    if (hiddenAudioRef.current) {
      if (hiddenAudioRef.current.paused) hiddenAudioRef.current.play();
      else hiddenAudioRef.current.pause();
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
      <IconButton
        onClick={togglePlay}
        color="primary"
        aria-label={isPlaying ? "pause" : "play"}
        sx={{
          border: "1px solid #1976d2",
          "&.Mui-disabled": { borderColor: "#bbb" },
        }}
      >
        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
      </IconButton>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          ref={waveContainerRef}
          sx={{
            width: "100%",
            minHeight: height,
            bgcolor: "#fafafa",
            borderRadius: 1,
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 0.5,
            minHeight: 18,
          }}
        >
          <Box sx={{ flex: 1 }}>
            {!waveReady && !waveError && (
              <Typography variant="caption" color="text.secondary">
                Loading waveform…
              </Typography>
            )}
            {waveError && (
              <Typography variant="caption" color="error">
                Waveform unavailable. Audio still playable.
              </Typography>
            )}
          </Box>
          {waveReady && (
            <Typography
              variant="caption"
              sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                bgcolor: "rgba(15, 23, 42, 0.7)",
                color: "#fff",
                fontVariantNumeric: "tabular-nums",
                fontSize: "0.72rem",
                letterSpacing: 0.3,
                pointerEvents: "none",
              }}
            >
              {formatTs(currentTime)} /{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {formatTs(duration)}
              </Box>
            </Typography>
          )}
        </Box>
      </Box>
      <audio
        ref={hiddenAudioRef}
        src={proxiedAudioUrl(src)}
        crossOrigin="anonymous"
        preload="auto"
        style={{ display: "none" }}
      />
    </Box>
  );
}

WaveformPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  height: PropTypes.number,
};

WaveformPlayer.defaultProps = {
  height: 60,
};
